import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    no_hp: '',
    tanggal_lahir: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  // Validasi input
  const validateInput = (data: { no_hp: string; tanggal_lahir: string }) => {
    if (!data.no_hp.trim() || !data.tanggal_lahir.trim()) {
      return 'Nomor HP dan tanggal lahir harus diisi';
    }
    
    // Validasi format nomor HP (minimal 10 digit, maksimal 15 digit)
    const phoneRegex = /^[0-9]{10,15}$/;
    if (!phoneRegex.test(data.no_hp.replace(/\s/g, ''))) {
      return 'Format nomor HP tidak valid (10-15 digit)';
    }
    
    // Validasi format tanggal lahir (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(data.tanggal_lahir)) {
      return 'Format tanggal lahir tidak valid (YYYY-MM-DD)';
    }
    
    // Validasi tanggal lahir tidak boleh di masa depan
    const birthDate = new Date(data.tanggal_lahir);
    const today = new Date();
    if (birthDate > today) {
      return 'Tanggal lahir tidak boleh di masa depan';
    }
    
    // Validasi usia minimal (misal 10 tahun)
    const age = today.getFullYear() - birthDate.getFullYear();
    if (age < 10) {
      return 'Usia minimal 10 tahun';
    }
    
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validasi input
      const validationError = validateInput(formData);
      if (validationError) {
        setError(validationError);
        setLoading(false);
        return;
      }

      const response = await fetch('https://event.petirs.id/backend/api/login.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          no_hp: formData.no_hp.trim(),
          tanggal_lahir: formData.tanggal_lahir
        }),
      });

      const data = await response.json();

      if (data.success) {
        await login(data.data);
        navigate('/dashboard');
      } else {
        setError(data.message || 'Login gagal');
      }
    } catch (err) {
      setError('Terjadi kesalahan koneksi. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Batasi panjang input
    if (name === 'no_hp' && value.length > 15) return;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Login Peserta
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Masuk ke sistem check-in event
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="no_hp" className="sr-only">
                Nomor HP
              </label>
              <input
                id="no_hp"
                name="no_hp"
                type="tel"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Nomor HP (contoh: 08123456789)"
                value={formData.no_hp}
                onChange={handleInputChange}
                maxLength={15}
              />
            </div>
            <div>
              <label htmlFor="tanggal_lahir" className="sr-only">
                Tanggal Lahir
              </label>
              <input
                id="tanggal_lahir"
                name="tanggal_lahir"
                type="date"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Tanggal Lahir"
                value={formData.tanggal_lahir}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    {error}
                  </h3>
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Memproses...' : 'Login'}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/panitia-login')}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              Login sebagai Panitia
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login; 